import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, App as AntdApp } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import deDE from 'antd/locale/de_DE';
import zhTW from 'antd/locale/zh_TW';
import jaJP from 'antd/locale/ja_JP';
import esES from 'antd/locale/es_ES';
import koKR from 'antd/locale/ko_KR';
import frFR from 'antd/locale/fr_FR';
import arEG from 'antd/locale/ar_EG';
import ptPT from 'antd/locale/pt_PT';
import ptBR from 'antd/locale/pt_BR';
import 'antd/dist/reset.css';
import App from './App';
import './i18n';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';

const FRONTEND_DEBUG_SESSION_ID = 'ubuntu-agents-fallback';

const reportFrontendDebug = (event, payload = {}) => {
  try {
    const token = window.localStorage?.getItem('token');
    if (!token) {
      return;
    }

    const body = JSON.stringify({
      session_id: FRONTEND_DEBUG_SESSION_ID,
      event,
      path: window.location?.pathname || '',
      search: window.location?.search || '',
      href: window.location?.href || '',
      ts: new Date().toISOString(),
      payload,
    });

    window.fetch('/api/auth/frontend-debug', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
};

if (typeof window !== 'undefined') {
  window.__allowtruReportFrontendDebug = reportFrontendDebug;
}

// 仅维护语言标记与图标，页面级 title/description 由路由元信息组件接管。
const updateDocumentShell = () => {
  try {
    if (typeof document !== 'undefined') {
      const htmlEl = document.documentElement;
      if (htmlEl) {
        htmlEl.lang = (i18n.language || 'zh');
      }
      const defaultIcon = `${process.env.PUBLIC_URL || ''}/favicon.png`;
      const nextIcon = defaultIcon;
      const iconLinks = document.querySelectorAll('link[rel="icon"]');
      if (iconLinks && iconLinks.length > 0) {
        iconLinks.forEach((link) => {
          if (link && link.href !== nextIcon) {
            link.href = nextIcon;
          }
        });
      }

      const appleTouch = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleTouch && appleTouch.href !== nextIcon) {
        appleTouch.href = nextIcon;
      }
    }
  } catch (e) {
    // 忽略元信息更新错误
    console.warn('Failed to update document meta by i18n:', e);
  }
};

// 初始化时与语言切换时更新
updateDocumentShell();
i18n.on('languageChanged', updateDocumentShell);

const getAntdLocale = (lng) => {
  const lang = String(lng || '').toLowerCase();
  if (lang.includes('zh-hant') || lang.includes('zh-hk') || lang.startsWith('zh-tw')) return zhTW;
  if (lang.startsWith('zh')) return zhCN;
  if (lang.startsWith('ja')) return jaJP;
  if (lang.startsWith('es')) return esES;
  if (lang.startsWith('ko')) return koKR;
  if (lang.startsWith('fr')) return frFR; // fr-CA 也归入 frFR
  if (lang.startsWith('ar')) return arEG; // ar-SA 映射到 arEG
  if (lang.startsWith('de')) return deDE;
  if (lang.startsWith('pt-br')) return ptBR;
  if (lang.startsWith('pt')) return ptPT;
  return enUS;
};

function Root() {
  const [lng, setLng] = React.useState(i18n.language || 'zh');
  React.useEffect(() => {
    const handler = (nextLng) => setLng(nextLng);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);
  return (
    <ConfigProvider 
      locale={getAntdLocale(lng)}
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
          fontSize: 14,
        },
      }}
    >
      <AntdApp>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);
